import type { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import type { ClientForSelector } from '@src/clients/ClientSelectOption';
import {
  filterClientSelectOptions,
  renderClientSelectOption,
} from '@src/clients/ClientSelectOption';
import { MultipleAutocomplete } from '@src/common/components/Autocomplete';
import type { SearchPermitFilters } from '@src/permits/services/PermitApplicationClient';

export type ClientOption = ClientForSelector;

interface Props {
  onFilterChange: (filters: SearchPermitFilters) => void;
  value: {
    name: string;
    value: string;
  }[];
  renderInput: (params: AutocompleteRenderInputParams) => JSX.Element;
  options?: ClientOption[];
  filterKey: 'billTo' | 'client';
}

export const ClientFilter = ({
  onFilterChange,
  value,
  renderInput,
  options,
  filterKey,
}: Props) => {
  const getClientOptionLabel = (option: unknown) => {
    return typeof option === 'string'
      ? option
      : ((option as ClientOption).name ?? '');
  };

  const sortClientsByName = (clients?: ClientOption[]) => {
    if (!clients) {
      return [];
    }
    return clients
      .slice()
      .sort((clientA, clientB) => clientA.name.localeCompare(clientB.name));
  };

  const isClientOption = (client: unknown): client is ClientOption =>
    client !== null &&
    typeof client === 'object' &&
    'name' in client &&
    'id' in client;

  const handleChange = (_: unknown, selectedValues: unknown) => {
    if (Array.isArray(selectedValues)) {
      const selectedClients = selectedValues.filter(isClientOption);
      const clientIds = selectedClients.map((client) => client.id);

      onFilterChange({
        [filterKey]: clientIds,
      } as Partial<SearchPermitFilters>);
    } else {
      onFilterChange({
        [filterKey]: [],
      } as Partial<SearchPermitFilters>);
    }
  };

  return (
    <MultipleAutocomplete
      value={value}
      onChange={handleChange}
      getOptionLabel={getClientOptionLabel}
      renderInput={renderInput}
      filterOptions={filterClientSelectOptions}
      renderOption={renderClientSelectOption}
      options={sortClientsByName(options).map((client) => ({
        name: client.name,
        id: client.id,
        doingBusinessAs: client.doingBusinessAs,
      }))}
    />
  );
};
